import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import { XMarkIcon, PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
}

interface Usuario {
  id: number;
  nombre: string;
  apellido: string;
  email: string;
  rol: 'ayudante' | 'jtp' | 'profesor' | 'admin';
}

const GestionarDocentesPage: React.FC = () => {
  const [selectedCurso, setSelectedCurso] = useState<Curso | null>(null);
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  // Fetch cursos (solo admin ve todos)
  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos'],
    queryFn: async () => {
      const response = await axios.get('/cursos');
      return response.data;
    },
  });

  // Fetch docentes asignados al curso seleccionado
  const { data: docentesAsignados, isLoading: loadingDocentes } = useQuery({
    queryKey: ['docentes-curso', selectedCurso?.id],
    queryFn: async () => {
      if (!selectedCurso) return [];
      const response = await axios.get(`/curso-docente/curso/${selectedCurso.id}/docentes`);
      return response.data;
    },
    enabled: !!selectedCurso,
  });

  // Fetch todos los docentes disponibles (ayudantes, jtp, profesores)
  const { data: todosDocentes } = useQuery({
    queryKey: ['todos-docentes'],
    queryFn: async () => {
      const response = await axios.get('/usuarios');
      return response.data.filter((u: Usuario) => 
        ['ayudante', 'jtp', 'profesor'].includes(u.rol)
      );
    },
  });

  // Asignar docente
  const asignarDocenteMutation = useMutation({
    mutationFn: async (usuarioId: number) => {
      const response = await axios.post('/curso-docente/asignar', {
        cursoId: selectedCurso?.id,
        usuarioId,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docentes-curso', selectedCurso?.id] });
      queryClient.invalidateQueries({ queryKey: ['cursos'] });
      setShowAsignarModal(false);
      showSuccess('Docente asignado', 'El docente ha sido asignado al curso exitosamente');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al asignar el docente';
      showError('Error', errorMessage);
    },
  });

  // Desasignar docente
  const desasignarDocenteMutation = useMutation({
    mutationFn: async (usuarioId: number) => {
      await axios.delete(`/curso-docente/desasignar/${selectedCurso?.id}/${usuarioId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['docentes-curso', selectedCurso?.id] });
      queryClient.invalidateQueries({ queryKey: ['cursos'] });
      showSuccess('Docente desasignado', 'El docente ha sido desasignado del curso exitosamente');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al desasignar el docente';
      showError('Error', errorMessage);
    },
  });

  const handleAsignarDocente = (usuarioId: number) => {
    asignarDocenteMutation.mutate(usuarioId);
  };

  const handleDesasignarDocente = (usuarioId: number) => {
    if (window.confirm('¿Está seguro de que desea desasignar este docente del curso?')) {
      desasignarDocenteMutation.mutate(usuarioId);
    }
  };

  const getRolLabel = (rol: string) => {
    const labels: { [key: string]: string } = {
      ayudante: 'Ayudante',
      jtp: 'JTP',
      profesor: 'Profesor',
    };
    return labels[rol] || rol;
  };

  const getRolColor = (rol: string) => {
    const colors: { [key: string]: string } = {
      ayudante: 'bg-blue-100 text-blue-800',
      jtp: 'bg-purple-100 text-purple-800',
      profesor: 'bg-green-100 text-green-800',
    };
    return colors[rol] || 'bg-gray-100 text-gray-800';
  };

  // Filtrar docentes no asignados
  const docentesNoAsignados = todosDocentes?.filter((docente: Usuario) => {
    return !docentesAsignados?.some((asignado: Usuario) => asignado.id === docente.id);
  }) || [];

  // Verificar que el usuario es admin
  if (user?.rol !== 'admin') {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-600">
          <p className="text-lg font-semibold">Acceso Denegado</p>
          <p className="text-sm mt-2">Solo los administradores pueden gestionar docentes de cursos.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestionar Docentes de Cursos</h2>
          <p className="text-sm text-gray-600 mt-1">
            Asigna docentes (ayudantes, JTPs, profesores) a los cursos
          </p>
        </div>
      </div>

      {/* Selector de curso */}
      <div className="bg-white rounded-lg shadow p-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Seleccionar Curso
        </label>
        <select
          value={selectedCurso?.id || ''}
          onChange={(e) => {
            const cursoId = parseInt(e.target.value);
            const curso = cursos?.find((c: Curso) => c.id === cursoId);
            setSelectedCurso(curso || null);
            setShowAsignarModal(false);
          }}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Seleccionar un curso</option>
          {cursos?.map((curso: Curso) => (
            <option key={curso.id} value={curso.id}>
              {formatCursoLabel(curso)}
            </option>
          ))}
        </select>
      </div>

      {selectedCurso && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">
                Docentes de {formatCursoNombreAnio(selectedCurso)}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {docentesAsignados?.length || 0} docente(s) asignado(s)
              </p>
            </div>
            <button
              onClick={() => setShowAsignarModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Asignar Docente
            </button>
          </div>

          {loadingDocentes ? (
            <div className="p-6 text-center text-gray-500">Cargando docentes...</div>
          ) : docentesAsignados?.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <UserGroupIcon className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              <p>No hay docentes asignados a este curso</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {docentesAsignados?.map((docente: Usuario) => (
                <div
                  key={docente.id}
                  className="px-6 py-4 flex justify-between items-center hover:bg-gray-50"
                >
                  <div>
                    <p className="font-medium text-gray-900">
                      {docente.nombre} {docente.apellido}
                    </p>
                    <p className="text-sm text-gray-600">{docente.email}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium ${getRolColor(
                        docente.rol
                      )}`}
                    >
                      {getRolLabel(docente.rol)}
                    </span>
                    <button
                      onClick={() => handleDesasignarDocente(docente.id)}
                      className="text-red-600 hover:text-red-800 p-2"
                      title="Desasignar docente"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal para asignar docente */}
      {showAsignarModal && selectedCurso && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Asignar Docente a {formatCursoNombreAnio(selectedCurso)}
              </h3>
              <button
                onClick={() => setShowAsignarModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {docentesNoAsignados.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <p>No hay docentes disponibles para asignar</p>
                  <p className="text-sm mt-2">
                    Todos los docentes ya están asignados a este curso
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {docentesNoAsignados.map((docente: Usuario) => (
                    <div
                      key={docente.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 flex justify-between items-center"
                    >
                      <div>
                        <p className="font-medium text-gray-900">
                          {docente.nombre} {docente.apellido}
                        </p>
                        <p className="text-sm text-gray-600">{docente.email}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${getRolColor(
                            docente.rol
                          )}`}
                        >
                          {getRolLabel(docente.rol)}
                        </span>
                        <button
                          onClick={() => handleAsignarDocente(docente.id)}
                          disabled={asignarDocenteMutation.isPending}
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {asignarDocenteMutation.isPending ? 'Asignando...' : 'Asignar'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionarDocentesPage;

